import { APITypes } from '../../primary-record-types';
import { useQuery } from '@tanstack/react-query';
import { getOrganizationClientRecordAuditLogsCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { getIntl } from '../../utils/intl';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationClientRecordAuditLogs', {
    errorLoadingAuditLogs: 'Error loading client activity history',
});

export type UseOrganizationClientRecordAuditLogsArgs = {
    organizationClientRecordId: string;
};

export type UseOrganizationClientRecordAuditLogsReturnValue = {
    isLoadingAuditLogs: boolean;
    isFetchingAuditLogs: boolean;
    auditLogs: APITypes.organizationService.ListOrganizationClientRecordAuditLogsResponse['items'];
};

export const useOrganizationClientRecordAuditLogs = ({
    organizationClientRecordId,
}: UseOrganizationClientRecordAuditLogsArgs): UseOrganizationClientRecordAuditLogsReturnValue => {
    const organizationId = useOrganizationId();

    const organizationClientRecordAuditLogsQuery = useQuery({
        queryKey: getOrganizationClientRecordAuditLogsCacheKey(organizationClientRecordId),
        queryFn: async () => {
            return appAPI.get<APITypes.organizationService.ListOrganizationClientRecordAuditLogsResponse>(
                urls.api.organizations.clientRecords.clientRecordAuditLogs(organizationId, organizationClientRecordId),
            );
        },
    });
    useQueryErrorNotify(organizationClientRecordAuditLogsQuery, intl('errorLoadingAuditLogs'));

    return {
        isLoadingAuditLogs: organizationClientRecordAuditLogsQuery.isLoading,
        isFetchingAuditLogs: organizationClientRecordAuditLogsQuery.isFetching,
        auditLogs: organizationClientRecordAuditLogsQuery.data?.items || [],
    };
};
